/*
Copyright (c) 2011 Andrei Mackenzie
Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

// Compute the edit distance between the two given strings
export function levenshteinDistance(
  s = "",
  t = "",
  caseInsensitive = true
): number {
  if (caseInsensitive) {
    s = s.toLowerCase();
    t = t.toLowerCase();
  }

  const m = s.length;
  const n = t.length;

  /* For all i and j, d[i][j] holds the string-alignment distance
   * between the first i characters of s and the first j characters of t.
   * Note that the array has (m+1)x(n+1) values.
   */
  const d: number[][] = [];
  for (let i = 0; i <= m; i++) {
    d[i] = [];
    d[i][0] = i;
  }
  for (let j = 0; j <= n; j++) {
    d[0][j] = j;
  }

  // Determine substring distances
  let cost = 0;
  for (let j = 1; j <= n; j++) {
    for (let i = 1; i <= m; i++) {
      cost = s.charAt(i - 1) == t.charAt(j - 1) ? 0 : 1; // Subtract one to start at strings' index zero instead of index one
      d[i][j] = Math.min(
        d[i][j - 1] + 1, // insertion
        Math.min(
          d[i - 1][j] + 1, // deletion
          d[i - 1][j - 1] + cost
        )
      ); // substitution

      if (
        i > 1 &&
        j > 1 &&
        s.charAt(i - 1) == t.charAt(j - 2) &&
        s.charAt(i - 2) == t.charAt(j - 1)
      ) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost); // transposition
      }
    }
  }
  // Return the strings' distance
  return d[m][n];
}

export function levenshteinPercentage(
  from = "",
  to = "",
  caseInsensitive = true
) {
  const distance = levenshteinDistance(from, to, caseInsensitive);
  if (!distance) return 100;
  return Math.round((1 - distance / Math.max(from.length, to.length)) * 100);
}
